import {
  type AccountOpeningDescriptor,
  type AccountOpeningFlowMode,
  type AccountOpeningGate,
  type AccountOpeningStep,
  type AccountOpeningStepId,
} from '../../../models/AccountOpeningDescriptor'
import { isItemConfirmed, isStepComplete, stepItems, type CollectionContext } from './collections'
import { operationSucceeded, type SubmissionOutcome } from './connectorResult'

export const resolveFlow = (descriptor: AccountOpeningDescriptor, mode: AccountOpeningFlowMode): AccountOpeningStep[] =>
  descriptor.steps.filter((step) => (step.modes ?? []).includes(mode))

export const irreversibleStep = (descriptor: AccountOpeningDescriptor): AccountOpeningStep | undefined =>
  descriptor.steps.find((step) => step.kind === 'operation' && step.irreversible)

export interface FlowState extends CollectionContext {
  confirmations: Record<AccountOpeningStepId, string[]>
  operationResults: Record<AccountOpeningStepId, SubmissionOutcome>
}

const confirmedRefs = (step: AccountOpeningStep, state: FlowState): string[] => state.confirmations[step.id] ?? []

export const isStepConfirmed = (step: AccountOpeningStep, state: FlowState): boolean => {
  if (step.kind !== 'collection' || !step.confirm) {
    return true
  }
  const refs = confirmedRefs(step, state)
  const items = stepItems(step, state)
  return items.length > 0 && items.every((item) => isItemConfirmed(item, refs))
}

const isStepDone = (step: AccountOpeningStep, state: FlowState): boolean => {
  switch (step.kind) {
    case 'collection':
      return isStepComplete(step, state) && isStepConfirmed(step, state)
    case 'operation':
      return operationSucceeded(state.operationResults[step.id])
    case 'handoff':
      return false
  }
}

export type StepStatus = 'inactive' | 'active' | 'done'

export const stepStatuses = (steps: AccountOpeningStep[], state: FlowState): Record<string, StepStatus> => {
  const declaredBy = new Map<AccountOpeningGate, AccountOpeningStep>()
  steps.forEach((step) => {
    if (step.gate && !declaredBy.has(step.gate)) {
      declaredBy.set(step.gate, step)
    }
  })

  const statuses: Record<string, StepStatus> = {}
  const resolving = new Set<string>()

  const statusOf = (step: AccountOpeningStep): StepStatus => {
    const cached = statuses[step.id]
    if (cached) {
      return cached
    }
    if (resolving.has(step.id)) {
      return 'inactive'
    }
    resolving.add(step.id)

    const open = (step.requires ?? []).every((gate) => {
      const declarer = declaredBy.get(gate)
      return declarer ? statusOf(declarer) === 'done' : false
    })

    resolving.delete(step.id)
    statuses[step.id] = !open ? 'inactive' : step.gate && isStepDone(step, state) ? 'done' : 'active'
    return statuses[step.id]
  }

  steps.forEach(statusOf)
  return statuses
}

export const isStepReached = (statuses: Record<string, StepStatus>, id: AccountOpeningStepId): boolean =>
  statuses[id] !== undefined && statuses[id] !== 'inactive'
